import { ModalProps } from '@chakra-ui/react'
import React, { ReactElement } from 'react'
import {
  Control,
  DefaultValues,
  FieldErrors,
  FieldValues,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form'
import FormContent from './FormContent'

export enum FullWidthModalAction {
  DRAFT = 'DRAFT',
  SAVE = 'SAVE',
}

interface ChildrenProps2 {
  screenIndex: number
  nextScreen: () => void
  prevScreen: () => void
}

export type FullWidthModalProps = Omit<ModalProps, 'children'> & {
  title: string
  children: ReactElement
}

export type InheritFullWidthModalProps<T> = Omit<
  FullWidthModalProps,
  'children' | 'title' | 'onClose'
> & {
  id?: string
  onClose: (value?: { data?: T; type?: FormMutationType }) => void
}

export type FormContentProps<T extends FieldValues> = {
  children: ReactElement | ReactElement[]
  navigations?: { label: string; isActive?: boolean }[]
  onSubmit: (data: T, action: FullWidthModalAction) => void
  onBack?: (data: T) => void
  handleSubmit: UseFormHandleSubmit<T, undefined>
  getValues: UseFormGetValues<T>
  hasDraft?: boolean
}

export type FormProps<T extends FieldValues> = {
  value?: T
  onSubmit: (value: T, type?: FullWidthModalAction) => void
  onBack?: (value: T) => void
  isLoadingSubmit?: boolean
  navigations?: { label: string; isActive?: boolean }[]
  children(props: FormChildrenProps<T>): ReactElement
  hasDraft?: boolean
}

export enum FormMutationType {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
}

export type OnCloseFormContainer<T> = (returnData?: {
  type: FormMutationType
  data?: T
}) => void

export type FormContainerProps<T> = {
  id?: string
  isOpen: boolean
  onClose: OnCloseFormContainer<T>
  loading?: boolean
}

interface FormChildrenProps<T extends FieldValues> {
  errors: FieldErrors<T>
  control: Control<T, any>
  register: UseFormRegister<T>
  setValue: UseFormSetValue<T>
  watch: UseFormWatch<T>
}

export enum SaveType {
  DRAFT = 'DRAFT',
  SAVE = 'SAVE',
}

type ChildrenProps<T extends FieldValues> = {
  register: UseFormRegister<T>
  watch: UseFormWatch<T>
  control: Control<T, any>
  errors: FieldErrors<T>
  setValue: UseFormSetValue<T>
  getValues: UseFormGetValues<T>
  trigger: UseFormTrigger<T>
}

export interface ModalContentProps<T extends FieldValues> {
  children: (props: ChildrenProps<T>) => ReactElement
  navigations?: { label: string; isActive?: boolean }[]
  isLoadingSave?: boolean
  hasDraft?: boolean
  onSubmit: (v: T, type: SaveType) => void
  onBack?: (value: T) => void
  value?: T
  defaultValues?: DefaultValues<T>
}

export type InheritModalContentProps<T extends FieldValues> = Omit<
  ModalContentProps<T>,
  'children'
>
